import React, { useCallback, useEffect, useState } from 'react';
import { SearchSection } from '../../../components/common/SearchSection';
import '../../../components/common/SearchSection.css';
import { LeaseTenantOption } from './types';
import { leasesApi } from './services/leaseApi';

interface LeaseTenantsSectionProps {
  selectedTenantIds: number[];
  onTenantsChange: (tenantIds: number[]) => void;
  includeLeaseId?: number;
  error?: string;
}

const MIN_TENANTS = 1;
const MAX_TENANTS = 10;

const formatTenantName = (tenant: LeaseTenantOption): string =>
  tenant.businessName?.trim() || `${tenant.firstName} ${tenant.lastName || ''}`.trim();

const LeaseTenantsSection: React.FC<LeaseTenantsSectionProps> = ({
  selectedTenantIds,
  onTenantsChange,
  includeLeaseId,
  error
}) => {
  const [tenants, setTenants] = useState<LeaseTenantOption[]>([]);

  useEffect(() => {
    leasesApi.getTenantOptions(includeLeaseId)
      .then(setTenants)
      .catch((err) => console.error('Error loading lease tenants:', err));
  }, [includeLeaseId]);

  const searchFunction = useCallback(async (searchTerm: string) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return tenants.slice(0, 10);
    }

    return tenants.filter((tenant) => {
      const haystack = [tenant.firstName, tenant.lastName, tenant.businessName]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    }).slice(0, 10);
  }, [tenants]);

  const getByIdFunction = useCallback(async (id: number) => {
    const tenant = tenants.find((t) => t.id === id);
    if (tenant) {
      return tenant;
    }
    const options = await leasesApi.getTenantOptions(includeLeaseId);
    const found = options.find((t) => t.id === id);
    if (!found) {
      throw new Error(`Tenant ${id} not found`);
    }
    return found;
  }, [tenants, includeLeaseId]);

  const renderResultItem = useCallback((tenant: LeaseTenantOption, onAdd: () => void, isDisabled?: boolean) => (
    <div className="search-result-item">
      <div className="search-result-item-content">
        {formatTenantName(tenant)}
      </div>
      <div className="search-result-item-actions">
        <button type="button" className="app-btn app-btn-primary btn-small" onClick={onAdd} disabled={isDisabled}>
          Add
        </button>
      </div>
    </div>
  ), []);

  const renderSelectedItem = useCallback((tenant: LeaseTenantOption, onRemove: () => void) => {
    const canRemove = selectedTenantIds.length > MIN_TENANTS;

    return (
      <div className="search-selected-item">
        <div className="search-selected-item-content">
          {formatTenantName(tenant)}
        </div>
        <div className="search-selected-item-actions">
          <button type="button" className="app-btn app-btn-secondary btn-small" onClick={onRemove} disabled={!canRemove}>
            Remove
          </button>
        </div>
      </div>
    );
  }, [selectedTenantIds.length]);

  return (
    <section className="form-section">
      <h3>Tenants ({selectedTenantIds.length})</h3>
      <div className="form-section-content">
        <SearchSection<LeaseTenantOption>
          selectionMode="multiple"
          selectedIds={selectedTenantIds}
          onSelectionChange={onTenantsChange}
          searchFunction={searchFunction}
          getByIdFunction={getByIdFunction}
          renderResultItem={renderResultItem}
          renderSelectedItem={renderSelectedItem}
          getItemId={(tenant) => tenant.id}
          label="Search Tenants"
          placeholder="Search tenant contacts..."
          entityType="lease-tenant"
          maxSelections={MAX_TENANTS}
          emptyStateMessage="No available tenant contacts found"
          selectedLabel="Selected Tenants"
        />
        {error && <div className="app-error-message">{error}</div>}
      </div>
    </section>
  );
};

export default LeaseTenantsSection;
